import type React from 'react';
import { useRef } from 'react';
import { Flex } from '@nula/core-ui';
import styles from './transcript-scrubber.module.css';

export interface TranscriptScrubberProps {
  positionMs: number;
  durationMs: number;
  /** Segment start times (ms) — drawn as faint tick marks along the track. */
  boundariesMs: number[];
  onSeek: (ms: number) => void;
  className?: string;
}

const pctOf = (ms: number, total: number): number =>
  total > 0 ? Math.min(100, Math.max(0, (ms / total) * 100)) : 0;

/**
 * Audio scrubber for the transcript player: a track with a brand fill, a
 * draggable head, and faint segment-boundary tick marks (a quiet nod to the
 * meeting's structure — not amplitude). Clicking anywhere on the track seeks
 * to that position; arrow keys nudge by 5s.
 */
export function TranscriptScrubber({
  positionMs,
  durationMs,
  boundariesMs,
  onSeek,
  className,
}: TranscriptScrubberProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const fillPct = pctOf(positionMs, durationMs);

  const seekToClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el || durationMs <= 0) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    onSeek(Math.round(ratio * durationMs));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      onSeek(Math.min(durationMs, positionMs + 5000));
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onSeek(Math.max(0, positionMs - 5000));
    }
  };

  return (
    <Flex
      align="center"
      data-testid="scrubber-track"
      ref={trackRef}
      role="slider"
      tabIndex={0}
      aria-label="Seek"
      aria-valuemin={0}
      aria-valuemax={Math.round(durationMs)}
      aria-valuenow={Math.round(positionMs)}
      onClick={(e) => seekToClientX(e.clientX)}
      onKeyDown={handleKeyDown}
      className={className ? `${styles.track} ${className}` : styles.track}
    >
      <Flex className={styles.rail} aria-hidden />
      {boundariesMs.map((ms, i) => (
        <Flex
          key={`${ms}-${i}`}
          aria-hidden
          data-testid="scrubber-tick"
          className={styles.tick}
          style={{ '--tick-left': `${pctOf(ms, durationMs)}%` } as React.CSSProperties}
        />
      ))}
      <Flex
        aria-hidden
        data-testid="scrubber-fill"
        className={styles.fill}
        style={{ '--fill-w': `${fillPct}%` } as React.CSSProperties}
      />
      <Flex
        aria-hidden
        className={styles.head}
        style={{ '--head-left': `${fillPct}%` } as React.CSSProperties}
      />
    </Flex>
  );
}
